'use client'
import { FC, useState, useEffect } from 'react'
import { Banner, Button, PasswordInput } from '@/root/ui/tuuleliinid'
import { useCustomRouter, useGetTranslations } from '@/root/shared-hooks'
import { validatePassword } from '@/root/shared-helpers'
import { passwordResetPerform } from '@/root/shared-api/auth'
import { getAuthErrorMessage } from '@/root/modules/tuuleliinid/LoginPage/helpers'
import { useForm, FieldValues } from 'react-hook-form'
import type { AuthError } from '@/root/shared-types/api/auth'

const REDIRECT_DELAY_MS = 3000

interface ResetPasswordFormProps {
  token: string
}

export const ResetPasswordForm: FC<ResetPasswordFormProps> = ({ token }) => {
  const { getTranslation } = useGetTranslations()
  const { locale, push } = useCustomRouter()
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FieldValues>({
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
    mode: 'onBlur',
  })

  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => push(`/${locale}/auth/login`), REDIRECT_DELAY_MS)
    return () => clearTimeout(timer)
  }, [success, locale, push])

  const passwordValue = watch('password')
  const confirmPasswordValue = watch('confirmPassword')

  const onSubmit = async (data: FieldValues) => {
    if (isLoading) return
    setIsLoading(true)
    setError(null)
    try {
      await passwordResetPerform(token, data.password, data.confirmPassword)
      setSuccess(true)
    } catch (err) {
      const authError = (err as { response?: { data?: AuthError } })?.response?.data ?? null
      setError(getAuthErrorMessage(authError, getTranslation))
    } finally {
      setIsLoading(false)
    }
  }

  const isDisabled = [
    isLoading,
    success,
    errors.password,
    !passwordValue,
    errors.confirmPassword,
    !confirmPasswordValue,
  ].some(Boolean)

  return (
    <>
      {success && <Banner variant="success" align="left" body={getTranslation('authResetPasswordSuccess')} className="mb-4" />}
      {error && <Banner variant="error" align="left" body={error} className="mb-4" />}

      <form className="flex w-full min-w-90 max-w-110 flex-col" onSubmit={handleSubmit(onSubmit)}>
        <PasswordInput
          label={getTranslation('recoveryPasswordLabel')}
          placeholder={getTranslation('authFieldPlaceholder')}
          error={errors.password?.message as string}
          {...register('password', {
            required: getTranslation('requiredError'),
            validate: (value) => validatePassword(value) || getTranslation('invalidPasswordText'),
          })}
        />

        <PasswordInput
          label={getTranslation('recoveryConfirmPasswordLabel')}
          placeholder={getTranslation('authFieldPlaceholder')}
          error={errors.confirmPassword?.message as string}
          {...register('confirmPassword', {
            required: getTranslation('requiredError'),
            validate: (value) => value === passwordValue || getTranslation('passwordsShouldMatchError'),
          })}
        />

        <Button
          className="mt-6 flex h-12 w-full items-center justify-center whitespace-nowrap rounded-lg text-lg font-bold"
          disabled={isDisabled}
          variant="gray"
          type="submit"
        >
          {isLoading ? getTranslation('authSending') : getTranslation('recoverySaveButton')}
        </Button>
      </form>
    </>
  )
}
